/* eslint-disable no-prototype-builtins,no-console */
import merge from "lodash.merge";
import keyBy from "lodash.keyby";
import values from "lodash.values";

export const STATUS_CODES = {
  100: "Continue",
  101: "Switching Protocols",
  102: "Processing", // RFC 2518, obsoleted by RFC 4918
  103: "Early Hints",
  200: "OK",
  201: "Created",
  202: "Accepted",
  203: "Non-Authoritative Information",
  204: "No Content",
  205: "Reset Content",
  206: "Partial Content",
  207: "Multi-Status", // RFC 4918
  208: "Already Reported",
  226: "IM Used",
  300: "Multiple Choices", // RFC 7231
  301: "Moved Permanently",
  302: "Found",
  303: "See Other",
  304: "Not Modified",
  305: "Use Proxy",
  307: "Temporary Redirect",
  308: "Permanent Redirect", // RFC 7238
  400: "Bad Request",
  401: "Unauthorized",
  402: "Payment Required",
  403: "Forbidden",
  404: "Not Found",
  405: "Method Not Allowed",
  406: "Not Acceptable",
  407: "Proxy Authentication Required",
  408: "Request Timeout",
  409: "Conflict",
  410: "Gone",
  411: "Length Required",
  412: "Precondition Failed",
  413: "Payload Too Large",
  414: "URI Too Long",
  415: "Unsupported Media Type",
  416: "Range Not Satisfiable",
  417: "Expectation Failed",
  418: "I'm a Teapot", // RFC 7168
  421: "Misdirected Request",
  422: "Unprocessable Entity", // RFC 4918
  423: "Locked", // RFC 4918
  424: "Failed Dependency", // RFC 4918
  425: "Unordered Collection", // RFC 4918
  426: "Upgrade Required", // RFC 2817
  428: "Precondition Required", // RFC 6585
  429: "Too Many Requests", // RFC 6585
  431: "Request Header Fields Too Large", // RFC 6585
  451: "Unavailable For Legal Reasons",
  500: "Internal Server Error",
  501: "Not Implemented",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
  505: "HTTP Version Not Supported",
  506: "Variant Also Negotiates", // RFC 2295
  507: "Insufficient Storage", // RFC 4918
  508: "Loop Detected",
  509: "Bandwidth Limit Exceeded",
  510: "Not Extended", // RFC 2774
  511: "Network Authentication Required", // RFC 6585
};

export const MAINTAIN_KEY = "MAINTAIN";

export const delay = (interval) =>
  new Promise((resolve) => {
    setTimeout(resolve, interval);
  });

export const getQueryParamByName = (name, url) => {
  if (!url && typeof window !== "undefined") url = window.location.href;
  name = name.replace(/[[\]]/g, "\\$&");
  const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};

export const getExtension = (url) => {
  if (!url) return "";
  return url.split(".").pop().split(/[#?]/)[0];
};

export const popupCenter = (url, title, w, h) => {
  // Fixes dual-screen position       Most browsers      Firefox
  const dualScreenLeft =
    window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop =
    window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
    ? document.documentElement.clientWidth
    : screen.width;
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
    ? document.documentElement.clientHeight
    : screen.height;

  const systemZoom = width / window.screen.availWidth;
  const left = (width - w) / 2 / systemZoom + dualScreenLeft;
  const top = (height - h) / 2 / systemZoom + dualScreenTop;
  const openFeatures = `scrollbars=yes, width=${w / systemZoom}, height=${
    h / systemZoom
  }, top=${top}, left=${left}`;
  const newWindow = window.open(url, title, openFeatures);
  newWindow.opener = null;

  // Puts focus on the newWindow
  if (window.focus) newWindow.focus();
};

export const move = (arr, originIdx, newIndex) => {
  while (originIdx < 0) {
    originIdx += arr.length;
  }
  while (newIndex < 0) {
    newIndex += arr.length;
  }
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length;
    while (k-- + 1) {
      arr.push(undefined);
    }
  }
  arr.splice(newIndex, 0, arr.splice(originIdx, 1)[0]);
  return arr;
};

/**
 * How to use ?
 * var a = ['a', 1, 'a', 2, '1'];
 * var unique = a.filter(filterUnique); // returns ['a', 1, 2, '1']
 *
 * @param value
 * @param index
 * @param self
 * @returns {boolean}
 */
export const filterUnique = (value, index, self) => {
  return self.indexOf(value) === index;
};

/**
 * How to use ?
 * arr.filter(filterUnique(['name', 'age', ...etc]))
 *
 * @param props
 * @returns {Function}
 */
export const filterUniqueObj = (props) => {
  const compare = (a, b) => {
    let prop;
    if (props) {
      for (let j = 0; j < props.length; j++) {
        prop = props[j];
        if (a[prop] !== b[prop]) {
          return false;
        }
      }
    } else {
      for (prop in a) {
        if (a[prop] !== b[prop]) {
          return false;
        }
      }
    }
    return true;
  };
  return (item, index, list) => {
    for (let i = 0; i < index; i++) {
      if (compare(item, list[i])) {
        return false;
      }
    }
    return true;
  };
};

export const checkIfMobile = (userAgent) => {
  let mobile = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series([46])0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br([ev])w|bumb|bw-([nu])|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do([cp])o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly([-_])|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-([mpt])|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c([- _agpst])|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac([ \-/])|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja([tv])a|jbro|jemu|jigs|kddi|keji|kgt([ /])|klon|kpt |kwc-|kyo([ck])|le(no|xi)|lg( g|\/([klu])|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t([- ov])|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30([02])|n50([025])|n7(0([01])|10)|ne(([cm])-|on|tf|wf|wg|wt)|nok([6i])|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan([adt])|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c([-01])|47|mc|nd|ri)|sgh-|shar|sie([-m])|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel([im])|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c([- ])|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
        a.substr(0, 4)
      )
    )
      mobile = true;
  })(userAgent);
  return mobile;
};

/**
 * personal-data => personalData
 * PERSONAL-DATA => personalData
 *
 * @param str
 * @returns {string}
 */
export const toCamelCase = (str) => {
  return (str || "")
    .split("-")
    .map((s, index) => {
      if (index > 0) {
        return `${s.slice(0, 1).toUpperCase()}${s.slice(1).toLowerCase()}`;
      }
      return s.toLowerCase();
    })
    .join("");
};

export const convertQueryObjToString = (query) => {
  const queryStr = [];

  for (const p in query)
    if (query.hasOwnProperty(p)) {
      queryStr.push(encodeURIComponent(p) + "=" + encodeURIComponent(query[p]));
    }
  return queryStr.join("&");
};

export const filterObjProps = (raw, allowed) => {
  return Object.keys(raw)
    .filter((key) => allowed.includes(key))
    .reduce((obj, key) => {
      obj[key] = raw[key];
      return obj;
    }, {});
};

export const parseJwt = (token) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

export const getAxiosErr = (err) => {
  let result = {
    message: "",
    statusCode: 500,
    code: 0,
  };
  if (err.response) {
    // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
    result.message = err.response.data;
    result.statusCode = err.response.status;
    result.contentType = err.response.headers["content-type"];
    result.maintainProperties = err.response.headers["x-properties"];
  } else if (err.request) {
    // 请求已经成功发起，但没有收到响应
    // `error.request` 在浏览器中是 XMLHttpRequest 的实例，
    // 而在node.js中是 http.ClientRequest 的实例
    if (err.request.response) {
      result.message = err.request.response;
    }
    if (err.request.status) {
      result.statusCode = err.request.status;
    }
    if (err.isAxiosError && !result.message) {
      err = err.toJSON();
      result.message = err.message;
    }
  } else {
    // 发送请求时出了点问题
    if (err.isAxiosError && !result.message) {
      err = err.toJSON();
    }
    result.message = err.message;
  }
  if (typeof result.message === "object") {
    const webapiErr = { ...result.message }; // webapi 包的錯誤物件
    result = {
      ...result,
      ...webapiErr,
    };
  }
  if (!result.message) {
    result.message = STATUS_CODES[result.statusCode];
  }
  console.error("============== getAxiosErr ==============");
  console.error("origin error:");
  console.error(err);
  console.error("handled error:");
  console.error(result);
  console.error("============== end ==============");
  return result;
};

export const getAxiosConfig = (context, config = {}) => {
  const { rootState, state } = context;
  let headers = {};
  if ((rootState || state).authModule.token) {
    headers.Authorization = "Bearer " + (rootState || state).authModule.token;
  }
  if (config.headers) {
    headers = {
      ...headers,
      ...config.headers,
    };
  }
  return {
    ...config,
    ...(Object.keys(headers).length > 0
      ? {
          headers: {
            ...headers,
          },
        }
      : {}),
  };
};

export const checkAxiosRes = (
  res,
  { handleMessages = () => true } = {
    handleMessages: () => true,
  }
) => {
  return checkAxiosData(
    {
      ...res.data,
      headers: res.headers,
    },
    {
      handleMessages,
    }
  );
};

export const checkAxiosData = (
  data,
  { handleMessages = () => true } = {
    handleMessages: () => true,
  }
) => {
  // check content-type
  if (
    data.headers &&
    data.headers["content-type"] &&
    !data.headers["content-type"].includes("application/json")
  ) {
    if (data.includes(`document.cookie="Sky-DDoS="`)) {
      // Sky-CDN needs to reload the page
      if (process && process.server) {
        throw new Error("Connection timeout");
      } else {
        window.location.reload();
        return false;
      }
    } else {
      throw new Error("Error parsing response data as JSON object");
    }
  }

  const messages = [];

  // check is maintain
  if (data.headers && data.headers["x-properties"]) {
    let properties;
    if (process && process.server) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      properties = require("buffer")
        .Buffer.from(data.headers["x-properties"], "base64")
        .toString();
    } else {
      properties = atob(data.headers["x-properties"]);
    }
    if (checkIsJSON(properties)) {
      properties = JSON.parse(properties);
      messages.push(
        `${MAINTAIN_KEY}::${properties.startTime}::${properties.endTime}`
      );
    }
  }

  return handleMessages(messages);
};

export function checkIsJSON(text) {
  return /^[\],:{}\s]*$/.test(
    text
      .replace(/\\["\\/bfnrtu]/g, "@")
      .replace(
        /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g,
        "]"
      )
      .replace(/(?:^|:|,)(?:\s*\[)+/g, "")
  );
}

export const compose = (...fns) =>
  fns.reduceRight(
    (prevFn, nextFn) =>
      (...args) =>
        nextFn(prevFn(...args)),
    (value) => value
  );

export const pipe = (...fns) =>
  fns.reduce(
    (prevFn, nextFn) =>
      (...args) =>
        nextFn(prevFn(...args)),
    (value) => value
  );

export const tap = (fns) => (data) => {
  fns(data);
  return data;
};

export const canUseWebP = () => {
  const elem = document.createElement("canvas");
  if (elem.getContext && elem.getContext("2d")) {
    // was able or not to get WebP representation
    return elem.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  }
  // very old browser like IE 8, canvas not supported
  return false;
};

export const imageIsExist = (url) =>
  new Promise((resolve) => {
    let img = new Image();
    img.onload = function () {
      if (this.complete === true) {
        resolve(true);
        img = null;
      }
    };
    img.onerror = function () {
      resolve(false);
      img = null;
    };
    img.src = url;
  });

/**
 *
 * sample:
 * loadElement({
 *   attributes: {
 *     src: 'https://genieedmp.com/dmp.js?c=5875&ver=2',
 *     async: true,
 *   },
 *   appendTarget: 'head'
 * }, function () {
 *   loadElement({
 *     attributes: {
 *       src: 'https://rt.gsspat.jp/e/conversion/lp.js?ver=2',
 *       type: 'text/javascript'
 *     }
 *   })
 * })
 *
 * @param options: Object, format: { name: String, appendTarget: String (only 'head' or 'body' value), attributes: Object }
 * @param cb: Function, completed callback
 */
export const loadElement = (options, cb = (f) => f) => {
  const ele = document.createElement(options.name || "script");
  Object.keys(options.attributes || {}).forEach(function (attribute) {
    ele.setAttribute(attribute, options.attributes[attribute]);
  });

  if ((options.appendTarget || "body") === "head") {
    const head = document.getElementsByTagName("head")[0];
    head.appendChild(ele);
  } else {
    document.body.appendChild(ele);
  }

  ele.onload = function () {
    if (typeof cb === "function") {
      cb();
    }
  };
};

export const toggleItemOfArray = (
  array = [],
  item = new Error(
    "The item parameter of the toggleItemOfArray function is required."
  )
) =>
  array.includes(item)
    ? array.filter((element) => element !== item)
    : [...array, item];

export const subscriber = (observer = (f) => f) => {
  if (typeof observer === "function") {
    return Object.values({
      next: observer,
    });
  }
  return Object.values({
    next: (f) => f,
    error: (f) => f,
    complete: (f) => f,
    ...observer,
  });
};

export const setIncrementInterval = (incrementInterval = (n) => n) => {
  return function (fns, start = 1, end = 0) {
    let n = start;
    const _setIncrementInterval = (fns, interval) => {
      const setTimeoutId = setTimeout(() => {
        let fnsResult = fns();
        if (!(fnsResult && fnsResult.hasOwnProperty("finally"))) {
          fnsResult = Promise.resolve();
        }
        fnsResult.finally(() => {
          if (n++ === end) {
            return clearTimeout(setTimeoutId);
          }
          _setIncrementInterval(fns, incrementInterval(n));
        });
      }, interval * 1000);
    };
    _setIncrementInterval(fns, n);
  };
};

export const fib = (n) => (n < 3 ? 1 : fib(n - 1) + fib(n - 2));

export const setFibInterval = (fns, start, end) =>
  setIncrementInterval(fib)(fns, start, end);

export const mergeObjArrByKey = (arr1, arr2, key) =>
  values(merge(keyBy(arr1, key), keyBy(arr2, key)));

export default {
  delay,
  getQueryParamByName,
  getExtension,
  popupCenter,
  move,
  filterUnique,
  filterUniqueObj,
  checkIfMobile,
  toCamelCase,
  convertQueryObjToString,
  filterObjProps,
  parseJwt,
  getAxiosErr,
  getAxiosConfig,
  checkAxiosRes,
  checkAxiosData,
  checkIsJSON,
  compose,
  pipe,
  tap,
  canUseWebP,
  imageIsExist,
  loadElement,
  toggleItemOfArray,
  subscriber,
  setIncrementInterval,
  fib,
  setFibInterval,
  mergeObjArrByKey,
};
